import {
  useUpdateCompanyMutation,
  CompanyDetails,
  useInvestorsQuery,
  usePaiReportRequestsQuery,
  useUpdateUserProfileMutation,
  useUserMetadataQuery,
} from 'models';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';
import { useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'utils/translation';
import { LanguageEditor } from 'Organisms/LanguageEditor';
import { EligibilityStatus } from 'Features/PortfolioView';
import { WorldIcon } from 'Tokens/Icons/Data';
import { SignOutIcon } from 'Tokens/Icons/Function';
import { hasSalesOrSupportRole } from 'utils/users';
import { FrameworkRefs } from 'containers/Esrs';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { Session } from '@nhost/nhost-js/session';

export function useUserSetting<T>(
  name: string,
  def: T
): [
  T,
  (value: T) => void,
  { loading: boolean },
  { called: boolean; error?: any; loading?: boolean },
] {
  const { user: currentUser } = useAuth();
  const nhost = useNhostClient();

  const { data: userData, loading } = useUserMetadataQuery({
    variables: { userId: currentUser?.id },
    skip: !currentUser?.id,
  });
  const userMetadata = useMemo(() => ({ ...userData?.user?.metadata }), [userData]);

  const [updateUser, updateUserInfo] = useUpdateUserProfileMutation();

  const userRef = useLatest(currentUser);
  const [optimisticValue, setOptimisticValue] = useState<T>(
    (currentUser?.metadata?.settings as any)?.[name] as T
  );

  useEffect(() => {
    if (currentUser && !loading) setOptimisticValue((userMetadata?.settings as any)?.[name] ?? def);
    // Note: intentionally omit `def` from deps because callers may pass
    // non-stable objects (e.g. `new Date(...)`) which would cause this
    // effect to run every render and create an update loop.
  }, [userMetadata, name, currentUser, loading]);

  const updateSetting = useCallback(
    (value: T) => {
      setOptimisticValue(value);
      const user = userRef.current;
      return (
        user &&
        updateUser({
          variables: {
            id: user.id,
            user: {
              metadata: {
                ...(user.metadata ?? {}),
                settings: {
                  ...((user?.metadata?.settings as object) ?? {}),
                  [name]: value,
                },
              },
            },
          },
          optimisticResponse: {
            user: {
              ...user,
              metadata: {
                ...(user.metadata ?? {}),
                settings: {
                  ...((user?.metadata?.settings as object) ?? {}),
                  [name]: value,
                },
              },
            },
          },
        }).then(() => {
          const currentSession = nhost.sessionStorage.get();

          if (currentSession && currentSession.user) {
            const updatedSession = {
              ...currentSession,
              user: {
                ...currentSession.user,
                metadata: {
                  ...currentSession.user.metadata,
                  settings: {
                    ...(currentSession.user.metadata?.settings ?? {}),
                    [name]: value,
                  },
                },
              },
            } as Session;

            nhost.sessionStorage.set(updatedSession);
          }
        })
      );
    },
    [name, updateUser]
  );
  return [
    optimisticValue ?? def,
    updateSetting,
    { loading: !currentUser || loading },
    updateUserInfo,
  ];
}
export function useUserSettings() {
  const { user: currentUser } = useAuth();
  const { data: userData, loading } = useUserMetadataQuery({
    variables: { userId: currentUser?.id },
    skip: !currentUser?.id,
  });
  const userMetadata = userData?.user?.metadata;

  const [updateUser] = useUpdateUserProfileMutation();
  const userRef = useLatest(currentUser);

  const [optimisticSettings, setOptimisticSettings] = useState<Record<string, any>>(
    currentUser?.metadata?.settings ?? {}
  );

  useEffect(() => {
    if (currentUser && !loading) {
      setOptimisticSettings(userMetadata?.settings ?? {});
    }
  }, [userMetadata, currentUser, loading]);

  useEffect(() => {
    // Sync optimisticSettings to local state, don't mutate currentUser
    if (currentUser && !loading) {
      setOptimisticSettings(userMetadata?.settings ?? {});
    }
  }, [userMetadata, currentUser, loading]);

  const updateUserSettings = useCallback(
    (newSettings: Record<string, any>) => {
      const updatedSettings = {
        ...optimisticSettings,
        ...newSettings,
      };

      setOptimisticSettings(updatedSettings);

      const user = userRef.current;
      if (!user) return;

      const currentSettings = {
        ...(user?.metadata?.settings ?? {}),
        ...newSettings,
      };

      return updateUser({
        variables: {
          id: user.id,
          user: {
            metadata: {
              ...(user.metadata ?? {}),
              settings: currentSettings,
            },
          },
        },
        optimisticResponse: {
          user: {
            ...user,
            metadata: {
              ...(user.metadata ?? {}),
              settings: currentSettings,
            },
          },
        },
      });
    },
    [optimisticSettings, updateUser, userRef]
  );

  return { optimisticSettings, updateUserSettings, loading: !currentUser || loading };
}

export function useChangeLanguage() {
  const { i18n } = useTranslation();
  const changeLanguage = useCallback((lang: string) => {
    i18n.changeLanguage(lang);
  }, []);
  return { changeLanguage, language: i18n.language };
}

export function useUserActions() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { signOut } = useAuth();
  const { changeLanguage, language } = useChangeLanguage();

  useEffect(() => {
    if (user?.locale && language != user?.locale && language) {
      changeLanguage(user.locale);
    }
  }, [language, user?.locale]);
  const {
    isOpen: isLanguageEditorOpen,
    onOpen: onLanguageEditorOpen,
    onClose: onLanguageEditorClose,
  } = useDisclosure();
  return useMemo(
    () => ({
      userActions: [
        {
          id: 'language',
          title: t('common:actions.language'),
          leftElement: <WorldIcon color="inherit" />,
          onClick: onLanguageEditorOpen,
        },
        {
          id: 'logout',
          title: t('common:actions.logOut'),
          leftElement: <SignOutIcon color="inherit" />,
          onClick: signOut,
        },
      ],
      user,
      languageEditor: user && (
        <LanguageEditor isOpen={isLanguageEditorOpen} onClose={onLanguageEditorClose} user={user} />
      ),
    }),
    [onLanguageEditorOpen, signOut, user]
  );
}

export function useUpdateCompanySettings() {
  const [updateCompany] = useUpdateCompanyMutation();
  return useCallback(
    ({ id, settings }: { id: string; settings: any }) =>
      updateCompany({
        variables: {
          company: {
            id,
            settings,
          },
        },
      }),
    [updateCompany]
  );
}

export function useCreateInitialCompany() {
  const nhost = useNhostClient();

  return useCallback(
    async (data: {
      userId: string;
      name?: string;
      isPortfolioOwner: boolean;
      isGroupOwner: boolean;
      currency?: string;
      createFirstBusinessUnit?: boolean;
      hasTaxonomyAccess?: boolean;
      hasESRSAccess?: boolean;
      hasVSMEAccess?: boolean;
      hasIFRSAccess?: boolean;
      hasTCFDAccess?: boolean;
      hasESRSOilAndGasAccess?: boolean;
      hasEntitySpecificAccess?: boolean;
      hasSASBAccess?: boolean;
    }) => {
      const {
        userId,
        name = '',
        isPortfolioOwner = false,
        isGroupOwner = false,
        currency,
        createFirstBusinessUnit = true,
        hasTaxonomyAccess = true,
        hasESRSAccess = false,
        hasVSMEAccess = false,
        hasIFRSAccess = false,
        hasTCFDAccess = false,
        hasESRSOilAndGasAccess = false,
        hasEntitySpecificAccess = false,
        hasSASBAccess,
      } = data;
      try {
        const res = await nhost.functions.post('/create-initial-company', {
          userId,
          name,
          isPortfolioOwner,
          isGroupOwner,
          currency,
          createFirstBusinessUnit,
          hasTaxonomyAccess,
          hasESRSAccess,
          hasVSMEAccess,
          hasIFRSAccess,
          hasTCFDAccess,
          hasESRSOilAndGasAccess,
          hasEntitySpecificAccess,
          hasSASBAccess,
        });
        const companyId = (res?.body as { companyId?: string })?.companyId;
        return companyId;
      } catch (e) {
        throw e;
      }
    },
    []
  );
}

export const useFeatureFlags = ({ company }: { company: CompanyDetails | undefined }) => {
  const { user } = useAuth();
  const { data: investorData, refetch: refetchInvestors } = useInvestorsQuery({
    variables: {
      companyId: company?.id,
    },
    skip: !company?.id,
  });
  const companyInvestors = useMemo(() => {
    return investorData?.investors ?? [];
  }, [investorData]);

  const { data: paiRequestsData } = usePaiReportRequestsQuery({
    variables: {
      companyId: company?.id,
    },
    skip: !company?.id,
  });

  const pai = useMemo(() => {
    if (company?.hasPaiAccess && company?.isPortfolioOwner) return true;

    if (!!paiRequestsData?.paiReportRequests?.length) {
      return true;
      // if (companyInvestors.some((i) => i.portfolio.ownerCompany?.hasPaiAccess)) return true;
      // else return false;
    }
    return false;
  }, [company, paiRequestsData, companyInvestors]);

  const assessments = useMemo(() => {
    const portfolioCompanies = company?.portfolioCompanies ?? [];
    const subsidiaries = company?.subsidiaries ?? [];
    const groupCompanies = company?.subsidiaries.map((s) => s.ownerGroupAssessment?.company) ?? [];

    if (!!portfolioCompanies.length || !!subsidiaries.length) {
      const isEligble = portfolioCompanies.some(
        (c) =>
          c.portfolio.ownerCompany.hasTaxonomyAccess &&
          (c.eligibilityStatus === EligibilityStatus.eligible ||
            c.eligibilityStatus === EligibilityStatus.notSure)
      );
      const isNotEligble = portfolioCompanies.every(
        (c) => c.eligibilityStatus === EligibilityStatus.notEligible
      );

      const groupHasTaxonomyAccess = groupCompanies.some((i) => i?.hasTaxonomyAccess);
      if (
        isEligble ||
        groupHasTaxonomyAccess ||
        (!!groupCompanies.length && !groupHasTaxonomyAccess && company?.hasTaxonomyAccess)
      ) {
        return true;
      } else if (isNotEligble) {
        return false;
      }
    }
    if (company?.hasTaxonomyAccess) {
      return true;
    }
    return false;
  }, [company, companyInvestors]);

  const hasAiAccess = useMemo(
    () => !!company?.hasAiAccess || hasSalesOrSupportRole(user?.roles),
    [company, user]
  );

  const canDownloadTaxonomyReport = useMemo(() => !!company?.canDownloadTaxonomyReport, [company]);
  const esrs = useMemo(() => !!company?.hasESRSAccess, [company]);
  const vsme = useMemo(() => !!company?.hasVSMEAccess, [company]);
  const ifrs = useMemo(() => !!company?.hasIFRSAccess, [company]);
  const tcfd = useMemo(() => !!company?.hasTCFDAccess, [company]);
  const esrsOilAndGas = useMemo(() => !!company?.hasESRSOilAndGasAccess, [company]);
  const entitySpecific = useMemo(() => !!company?.hasEntitySpecificAccess, [company]);
  const sasb = useMemo(() => !!company?.hasSASBAccess, [company]);

  const hasSustainabilityAccess = useMemo(
    () => esrs || vsme || ifrs || tcfd || esrsOilAndGas || entitySpecific || sasb,
    [esrs, vsme, ifrs, tcfd, esrsOilAndGas, entitySpecific, sasb]
  );

  const hasAuditLogsAccess = useMemo(() => hasSalesOrSupportRole(user?.roles), [user]);

  const hasBulkUploadAccess = useMemo(
    () => !!company?.hasBulkUploadAccess || hasSalesOrSupportRole(user?.roles),
    [company, user]
  );

  const fileUploadBlockList = [
    'e99ec3bb-6e67-4c92-9468-74f6ff7845ae',
    'a5124a38-ce46-4d9f-8f0c-2d48fd59e826',
    '572003e6-78ce-4cc4-a6db-0380a3fea881',
    'f42fbf51-9a8b-43b8-a97a-c49947855e79',
    'e346bf75-a7e2-491f-8b12-ba90728d8e92', // staging test company
  ];

  const drive = useMemo(() => !fileUploadBlockList.includes(company?.id), [company]);

  const canDownloadEsrsReport = useMemo(
    () => !!company?.canDownloadEsrsReport || hasSalesOrSupportRole(user?.roles),
    [company, user]
  );

  useEffect(() => {
    if (company) {
      refetchInvestors();
    }
  }, [company, refetchInvestors]);

  const companyFrameworks = useMemo(() => {
    const frameworks = [];
    if (esrs) frameworks.push(FrameworkRefs.esrs);
    if (vsme) frameworks.push(FrameworkRefs.vsme);
    if (ifrs) frameworks.push(FrameworkRefs.ifrs);
    if (tcfd) frameworks.push(FrameworkRefs.tcfd);
    if (esrsOilAndGas) frameworks.push(FrameworkRefs['esrs-oil-and-gas']);
    if (entitySpecific) frameworks.push(FrameworkRefs['entity-specific']);
    if (sasb) frameworks.push(FrameworkRefs.sasb);

    return frameworks;
  }, [esrs, vsme, ifrs, tcfd, esrsOilAndGas, entitySpecific, sasb]);

  return {
    pai,
    assessments,
    drive,
    canDownloadTaxonomyReport,
    esrs,
    vsme,
    ifrs,
    tcfd,
    esrsOilAndGas,
    entitySpecific,
    sasb,
    hasSustainabilityAccess,
    hasAiAccess,
    hasAuditLogsAccess,
    companyFrameworks,
    canDownloadEsrsReport,
    hasBulkUploadAccess,
  };
};

const HIDE_MODULES_PATH_CONFIG: Record<string, { embedUrls: string[]; directUrls: string[] }> = {
  esrs: {
    embedUrls: ['stratsys.com', 'stratsys.se', 'stratsys.net', 'novata.com'],
    directUrls: [],
  },
};

export const useHiddenFlags = () => {
  const hiddenModules: Record<string, boolean> = useMemo(
    () =>
      Object.entries(HIDE_MODULES_PATH_CONFIG).reduce((flags, [modulePath, config]) => {
        const hideDirectly = config.directUrls.some((link) =>
          window.location.origin.includes(link)
        );
        const hideWhenEmbedded = config.embedUrls.some((link) =>
          (document.location.ancestorOrigins?.[0] || document.referrer).includes(link)
        );
        const shouldHide = hideDirectly || hideWhenEmbedded;
        return { ...flags, [modulePath]: shouldHide };
      }, {}),

    [window.location.origin, document.location.ancestorOrigins?.[0], document.referrer]
  );

  return hiddenModules;
};
